import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';

import RavenErrorBoundary from '../../../../components/RavenErrorBoundary';
import styles from './content.scss';
import { update } from '../../../../ducks/app';

const propTypes = {
  children: PropTypes.node,
  id: PropTypes.string,
  transparent: PropTypes.bool,
  onRefresh: PropTypes.func,
  pullDistance: PropTypes.number,
  maxDistance: PropTypes.number,
  dispatch: PropTypes.func,
};

const defaultProps = {
  pullDistance: 60,
  maxDistance: 150,
};

const getPosition = (e) => {
  const p = { x: 0, y: 0 };

  if (typeof e.changedTouches !== 'undefined') {
    const touch = e.changedTouches[0];
    p.x = touch.screenX;
    p.y = touch.screenY;
  } else {
    p.x = e.screenX;
    p.y = e.screenY;
  }

  return p;
};

const easeOutQuad = (t) => t * (2 - t);

class PullToRefresh extends React.Component {
  constructor(props) {
    super(props);

    this.ref = React.createRef();
    this.state = {
      swiping: false,
      loading: false,
    };
  }

  componentDidMount() {
    if (this.ref.current) {
      this.ref.current.addEventListener('touchstart', this.onSwipeStart);
      this.ref.current.addEventListener('touchmove', this.onSwipe);
      this.ref.current.addEventListener('touchend', this.onSwipeEnd);

      this.ref.current.addEventListener('mousedown', this.onSwipeStart);
      this.ref.current.addEventListener('mousemove', this.onSwipe);
      this.ref.current.addEventListener('mouseup', this.onSwipeEnd);
      this.ref.current.addEventListener('mouseout', this.onSwipeEnd);
    }
  }

  componentWillUnmount() {
    if (this.ref.current) {
      this.ref.current.removeEventListener('touchstart', this.onSwipeStart);
      this.ref.current.removeEventListener('touchmove', this.onSwipe);
      this.ref.current.removeEventListener('touchend', this.onSwipeEnd);

      this.ref.current.removeEventListener('mousedown', this.onSwipeStart);
      this.ref.current.removeEventListener('mousemove', this.onSwipe);
      this.ref.current.removeEventListener('mouseup', this.onSwipeEnd);
      this.ref.current.removeEventListener('mouseout', this.onSwipeEnd);
    }
  }

  onSwipeStart = (e) => {
    if (this.props.transparent) {
      return;
    }

    const pStart = getPosition(e);

    this.setState({ swiping: pStart });
    this.props.dispatch(update({ pullToRefresh: true }));
  }

  onSwipe = (e) => {
    const { pullDistance, maxDistance } = this.props;

    this.setState(({ swiping }) => {
      if (!swiping) {
        return {};
      }

      if (this.ref.current.scrollTop !== 0) {
        return { swiping: false, dY: 0 };
      }

      const pStop = getPosition(e);
      let dY = pStop.y - swiping.y;
      if (dY < 0) {
        dY = 0;
      }

      if (dY > pullDistance) {
        const d = Math.min(1, ((dY - pullDistance) / maxDistance));
        dY = pullDistance + (easeOutQuad(d) * (maxDistance - pullDistance));
      }

      return { dY };
    });
  }

  onSwipeEnd = (e) => {
    const { pullDistance, dispatch } = this.props;

    let { relatedTarget } = e;
    while (relatedTarget) {
      // go up the parent chain and check if we're still inside
      // then that's an internal transition, ignore it
      if (relatedTarget === this.ref.current) {
        return;
      }

      relatedTarget = relatedTarget.parentNode;
    }

    this.setState(({ swiping }) => {
      if (!swiping) {
        return {};
      }

      const pStop = getPosition(e);
      const dY = pStop.y - swiping.y;

      if (dY < pullDistance) {
        // the pull was not long enough, cancel
        dispatch(update({ pullToRefresh: false }));
        return { dY: 0, swiping: false };
      }

      const { onRefresh } = this.props;

      // Trigger the refresh (expect a promise)
      onRefresh().then(() => {
        this.setState({ dY: 0, loading: false });
        dispatch(update({ pullToRefresh: false }));
      });
      return { dY: pullDistance, swiping: false, loading: true };
    });
  }

  render = () => {
    const {
      children,
      id,
      transparent,
      pullDistance,
    } = this.props;
    const {
      dY,
      swiping,
      loading,
    } = this.state;

    return (
      <div
        className={classNames(
          styles.ptrContainer,
          transparent && styles.transparent,
        )}
      >
        <div
          className={styles.ptrStatus}
          style={{ height: dY, transition: swiping ? '' : 'height 200ms ease-in-out' }}
        >
          {loading && (
            <i className={classNames(
              'far fa-spinner-third',
              [styles.rotate],
            )}
            />
          )}
          {!loading && dY < pullDistance && 'Pull to refresh'}
          {!loading && dY >= pullDistance && 'Release to refresh'}
        </div>
        <div
          id={id}
          className={classNames(styles.ptr, styles.content, transparent && styles.transparent)}
          ref={this.ref}
          style={{ top: dY, transition: swiping ? '' : 'top 200ms ease-in-out' }}
        >
          <RavenErrorBoundary>
            {children}
          </RavenErrorBoundary>
        </div>
      </div>
    );
  }
}

PullToRefresh.propTypes = propTypes;
PullToRefresh.defaultProps = defaultProps;

export default connect()(PullToRefresh);
